import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {Box, Paper, Stack, Typography, useTheme} from '@mui/material';
import {alpha} from '@mui/material/styles';
import {Dayjs} from 'dayjs';

import {CalendarItem, groupByDay, timeLabel} from './calendarModel';

interface Props {
    from: Dayjs;
    to: Dayjs;
    items: CalendarItem[];
    now: Dayjs;
    onSelect: (item: CalendarItem) => void;
}

/**
 * Tagesliste — die Ansicht für schmale Bildschirme.
 *
 * Ein Zeitraster mit sieben Spalten ist auf einem Telefon unbrauchbar; deshalb dieselbe Auswahl
 * an Daten, aber untereinander. Tage ohne Einträge bleiben stehen, damit der Zeitraum lückenlos
 * lesbar ist.
 */
export const AgendaList: React.FC<Props> = ({from, to, items, now, onSelect}) => {
    const {t, i18n} = useTranslation();
    const theme = useTheme();

    const byDay = useMemo(() => groupByDay(items), [items]);
    const todayKey = now.format('YYYY-MM-DD');

    const days = useMemo(() => {
        const out: Dayjs[] = [];
        for (let d = from; !d.isAfter(to, 'day'); d = d.add(1, 'day')) out.push(d);
        return out;
    }, [from, to]);

    const dayLabel = (day: Dayjs) =>
        new Intl.DateTimeFormat(i18n.language, {weekday: 'short', day: '2-digit', month: '2-digit'})
            .format(day.toDate());

    return (
        <Stack spacing={1}>
            {days.map((day) => {
                const key = day.format('YYYY-MM-DD');
                const dayItems = byDay.get(key) ?? [];
                const isToday = key === todayKey;
                const isWeekend = day.day() === 0 || day.day() === 6;

                return (
                    <Paper key={key} variant="outlined" sx={{
                        p: 1.25,
                        borderColor: isToday ? 'primary.main' : undefined,
                        bgcolor: isWeekend && dayItems.length === 0 ? 'action.hover' : undefined,
                    }}>
                        <Typography variant="subtitle2" color={isToday ? 'primary' : 'text.primary'}
                                    sx={{mb: dayItems.length ? 0.75 : 0}}>
                            {dayLabel(day)}
                        </Typography>

                        {dayItems.length === 0 && (
                            <Typography variant="body2" color="text.disabled">
                                {t('calendar.noEntries')}
                            </Typography>
                        )}

                        <Stack spacing={0.5}>
                            {dayItems.map((item) => (
                                <Box key={item.key} onClick={() => onSelect(item)} sx={{
                                    display: 'flex',
                                    gap: 1,
                                    alignItems: 'baseline',
                                    cursor: 'pointer',
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 0.5,
                                    borderLeft: 3,
                                    borderColor: `${item.color}.main`,
                                    bgcolor: alpha(theme.palette[item.color].main, 0.12),
                                }}>
                                    <Typography variant="caption" color="text.secondary" sx={{
                                        flexShrink: 0,
                                        minWidth: 84,
                                        fontVariantNumeric: 'tabular-nums',
                                    }}>
                                        {timeLabel(item, t('calendar.allDay'))}
                                    </Typography>
                                    <Box sx={{minWidth: 0}}>
                                        <Typography variant="body2" sx={{fontWeight: 500}}>
                                            {item.title}
                                        </Typography>
                                        {item.subtitle && (
                                            <Typography variant="caption" color="text.secondary">
                                                {item.subtitle}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            ))}
                        </Stack>
                    </Paper>
                );
            })}
        </Stack>
    );
};

export default AgendaList;
